import React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { Button as MantineButton, Center, Loader, Modal } from '@mantine/core';
import { gaps } from 'components/layout/stack';
import { observable } from 'mobx';
import Fa from 'components/fa';
import { AppUI } from '@seedlang/state';

const TextFileContent = styled.pre`
  margin: 0;
  max-height: 75vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.5;
`;

const ModalMessage = styled.div`
  padding: ${gaps.md} 0;
`;

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${gaps.md};
  justify-content: space-between;
  padding-right: ${gaps.md};
  width: 100%;

  @media screen and (max-width: 600px) {
    align-items: flex-start;
    flex-direction: column;
    gap: ${gaps.xs};
  }
`;

const TitleText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media screen and (max-width: 600px) {
    white-space: normal;
  }
`;

export const downloadLinkTitle = downloadLink => downloadLink.title;

export const isTextFileLink = (href) => {
  if (!href) return false;

  try {
    return new URL(href, window.location.href).pathname.toLowerCase().endsWith('.txt');
  } catch (_error) {
    return href.split('?')[0].toLowerCase().endsWith('.txt');
  }
};

@observer
class DownloadLinkTextFileModal extends React.Component {
  @observable opened = false;
  @observable title = '';
  @observable href = '';
  @observable content = '';
  @observable error = null;
  @observable isLoading = false;

  open = async (downloadLink) => {
    this.opened = true;
    this.title = downloadLinkTitle(downloadLink);
    this.href = downloadLink.text;
    this.content = '';
    this.error = null;
    this.isLoading = true;

    try {
      const response = await fetch(`/api/download_links/text_file?url=${encodeURIComponent(downloadLink.text)}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || `Unable to load file (${response.status})`);
      }
      this.content = data.content;
    } catch (error) {
      this.error = error.message || 'Sorry, this text file could not be loaded.';
    } finally {
      this.isLoading = false;
    }
  }

  close = () => {
    this.opened = false;
    this.title = '';
    this.href = '';
    this.content = '';
    this.error = null;
    this.isLoading = false;
  }

  renderTitle() {
    return (
      <ModalTitle>
        <TitleText>{this.title}</TitleText>
        <MantineButton
          component='a'
          href={this.href}
          target='_blank'
          rel='noopener noreferrer'
          size='xs'
          variant='light'
          color={AppUI.site.accentColor}
          leftSection={<Fa i='download' />}
        >
          Download
        </MantineButton>
      </ModalTitle>
    );
  }

  render() {
    return (
      <Modal
        opened={this.opened}
        onClose={this.close}
        title={this.renderTitle()}
        size='90%'
        styles={{ title: { flex: 1 } }}
      >
        {
          this.isLoading &&
            <Center py='md'>
              <Loader />
            </Center>
        }
        {
          this.error &&
            <ModalMessage>{this.error}</ModalMessage>
        }
        {
          !this.isLoading && !this.error &&
            <TextFileContent>{this.content}</TextFileContent>
        }
      </Modal>
    );
  }
}

export default DownloadLinkTextFileModal;
